import { useState, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useAddressSearch } from '@/hooks/useAddressSearch';
import { HereAddressType } from '@/api/apis/here-maps';
import { LocationFormType, NameByValue } from '@/types';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { getShortTzByIanaName } from '@/utils/helpers/date.helpers';
import { CountryEntryView } from '@/api/apis/facilities';
import { NA_REGION_ID } from '@/constants/serviceRegion.constants';

const getCountryState = (
  country: string,
  state: string | null,
  composedSubdivisionsList: Record<string, NameByValue[]>,
) => {
  if (!state) return '';
  const list = composedSubdivisionsList[country];
  const foundState = list?.find((item) => item.value === state);
  return foundState ? foundState.name : '';
};

export const formatAddressLine = (
  location: HereAddressType,
  countryIso2: string,
  countriesByIso2: Record<string, CountryEntryView>,
) => {
  if (!location.street || !location.houseNumber) {
    return location.label.split(',')[0];
  }
  // Linking to service region can be not the best option, but since we have only one region with house number first format, we can use country iso2 to determine the format
  const isHouseNumberFirst = countriesByIso2[countryIso2]?.serviceRegionId === NA_REGION_ID;

  return isHouseNumberFirst
    ? `${location.houseNumber} ${location.street}`
    : `${location.street}, ${location.houseNumber}`;
};

export const useAddressForm = () => {
  const [initAddressSearch, setInitAddressSearch] = useState(false);
  const { composedSubdivisionsList, countriesByIso2 } = useCountriesData();

  const { setValue, control } = useFormContext<LocationFormType>();
  const [addressLine, state, country] = useWatch({
    control,
    name: ['addressLine', 'state', 'country'],
  });

  const stateList = getCountryState(country, state, composedSubdivisionsList);

  const { list: addressList } = useAddressSearch(
    { address: addressLine, countryCode: country, stateName: stateList || state || '' },
    initAddressSearch,
  );

  const stateSelectList = useMemo(() => {
    if (!country) return [];
    const countrySubdivisions = composedSubdivisionsList[country] || [];
    return [...countrySubdivisions].sort((a, b) => a.name.localeCompare(b.name));
  }, [composedSubdivisionsList, country]);

  const onAddressSelect = useCallback(
    (addressText: string, withIanaTimezone = false) => {
      const addressItem = addressList.find((a) => a.id === addressText);
      if (addressItem) {
        setInitAddressSearch(false);

        const { address: location, position, timeZone } = addressItem;

        setValue('addressId', addressItem.id);
        setValue('addressLine', formatAddressLine(location, country, countriesByIso2), {
          shouldValidate: true,
        });
        setValue('city', location.city, { shouldValidate: true });
        setValue('postalCode', location.postalCode, { shouldValidate: true });
        setValue('longitude', position.lng, { shouldValidate: true });
        setValue('latitude', position.lat, { shouldValidate: true });

        if (withIanaTimezone) {
          const timezone = getShortTzByIanaName(timeZone.name);
          setValue('timezoneIANA', timeZone.name || '');
          setValue('timezone', timezone || '');
        }
      }
    },
    [addressList, setValue, country, countriesByIso2],
  );

  const onAddressInputChange = useCallback(
    (text: string, withIanaTimezone = false) => {
      setValue('addressLine', text);
      setValue('addressId', '');
      setValue('city', '');
      setValue('postalCode', '');
      setValue('longitude', null);
      setValue('latitude', null);
      setInitAddressSearch(true);

      if (withIanaTimezone) {
        setValue('timezone', '');
        setValue('timezoneIANA', '');
      }
    },
    [setValue],
  );

  const onStateInputChange = useCallback(
    (text: string, switchTimezone = true) => {
      if (text === state) return;

      setValue('state', text, { shouldValidate: true });
      setValue('addressId', '');
      setValue('addressLine', '', { shouldValidate: true });
      setValue('city', '');
      setValue('postalCode', '');
      setValue('longitude', null);
      setValue('latitude', null);

      if (switchTimezone) {
        setValue('timezone', '');
        setValue('timezoneIANA', '');
      }
      setInitAddressSearch(true);
    },
    [setValue, state],
  );

  const onCountryInputChange = useCallback(
    (text: string, switchTimezone = true) => {
      setValue('country', text, { shouldValidate: true });
      setValue('state', null, { shouldValidate: true });
      setValue('addressId', '');
      setValue('addressLine', '');
      setValue('city', '');
      setValue('postalCode', '');
      setValue('longitude', null);
      setValue('latitude', null);

      if (switchTimezone) {
        setValue('timezone', '');
        setValue('timezoneIANA', '');
      }

      setInitAddressSearch(true);
    },
    [setValue],
  );

  const onRegionSelectChange = (regionId: string, formattedRegionsList: NameByValue[]) => {
    const currentRegion = formattedRegionsList.find((h) => h.value === regionId);

    if (currentRegion) {
      setValue('region', currentRegion.value as string, { shouldValidate: true });
    }
  };

  return {
    addressValue: addressLine,
    addressList,
    stateSelectList,
    onAddressSelect,
    onAddressInputChange,
    onStateInputChange,
    onRegionSelectChange,
    onCountryInputChange,
  };
};
